import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { acceptTerms, getTermsStatus } from '../service'
import { apiErrorText } from '../service/coaching'
import {
  clearSignUpAcceptance,
  signUpAcceptanceVersion,
  termsGateDecision
} from '../service/terms'
import { TERMS_PATH, TERMS_UPDATED } from '../constants/legal'
import { useAuth } from '../auth/AuthContext'

// Records Terms of Use acceptance for signed-in users. The API is asked whether the current
// version was accepted; only an explicit "not accepted" opens the dialog, so an error or an
// unexpected response never locks anyone out. An acceptance ticked at sign-up by this same
// account on this device is recorded without asking again.
export default function TermsGate () {
  const { signOut, principal } = useAuth()
  const username = principal?.username || null
  const [open, setOpen] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    const check = async () => {
      let status
      try {
        status = await getTermsStatus()
      } catch (err) {
        console.warn('The Terms of Use status could not be checked.', err)
        return
      }
      // Signed out (or another account signed in) while the check was running.
      if (!active) return
      const decision = termsGateDecision(status, signUpAcceptanceVersion(username))
      if (decision === 'unknown') {
        console.warn('The Terms of Use status response was not understood.')
        return
      }
      if (decision === 'outdated') {
        // The API already asks for newer terms than this page shows; accepting from here would
        // be refused. The next page load (with the new terms) asks.
        console.warn('The Terms of Use were updated; reload the page to review them.')
        return
      }
      if (decision === 'accepted') {
        clearSignUpAcceptance()
        return
      }
      if (decision === 'accept_sign_up') {
        try {
          await acceptTerms('sign_up')
          clearSignUpAcceptance()
        } catch (err) {
          // Kept for the next visit; uploads still record acceptance themselves.
          console.warn('The sign-up acceptance of the Terms of Use could not be recorded.', err)
        }
        return
      }
      setOpen(true)
    }
    check()
    return () => { active = false }
  }, [username])

  const accept = async () => {
    setBusy(true)
    setError(null)
    try {
      await acceptTerms('sign_in')
      setOpen(false)
    } catch (err) {
      setError(apiErrorText(err, 'Your acceptance could not be saved. Try again in a moment.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      aria-labelledby="terms-gate-title"
      aria-describedby="terms-gate-description"
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="terms-gate-title">Accept the Terms of Use</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography id="terms-gate-description" variant="body2" color="text.secondary">
            To keep using Highlights, read and accept the Terms of Use (last updated {TERMS_UPDATED}).
            They cover what you may upload, other people in your videos, how videos are used and
            how to ask for removal.
          </Typography>
          <Link component={RouterLink} to={TERMS_PATH} target="_blank" rel="noopener">
            Read the Terms of Use
          </Link>
          <FormControlLabel
            control={<Checkbox checked={agreed} disabled={busy} onChange={event => setAgreed(event.target.checked)} />}
            label="I agree to the Terms of Use"
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={busy} onClick={signOut}>Sign out</Button>
        <Button variant="contained" disabled={!agreed || busy} onClick={accept}>
          {busy ? 'Saving...' : 'Accept and continue'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
